import { FormGroup } from '@angular/forms';

import { SubmissionConstants } from '../../models/constants/submissions-constants';
import {
  FormRule,
  FormRuleEffect,
  FormRuleFactory,
  RuleAction,
} from 'src/app/shared/forms';

export class HeatPumpDocumentRules {
  constructor(
    private ruleFact: FormRuleFactory,
    private submissionsConst: SubmissionConstants,
  ) {}

  getV2CustomRules(subValues: FormGroup): FormRule[] {
    const designPackage = subValues?.get('designPackage')?.value;
    const status = subValues?.get('status')?.value;
    const isHeatPumpDocumentOnFile = subValues?.get(
      'isHeatPumpDocumentOnFile',
    )?.value;

    const applyEffect = (action: RuleAction, value?: string) =>
      this.createBaseEffect(action, 'isHeatPumpDocumentOnFile', value);

    const shouldMakeHeatPumpDocumentRequired =
      (status === this.submissionsConst.statusReadyToSubmit ||
        status === this.submissionsConst.statusSubmittedManually) &&
      `${designPackage}`.endsWith('-EHP') &&
      !isHeatPumpDocumentOnFile;

    const shouldClearHeatPumpDocOnFile =
      (status === this.submissionsConst.statusReadyToSubmit ||
        status === this.submissionsConst.statusSubmittedManually) &&
      !`${designPackage}`.endsWith('-EHP') &&
      !isHeatPumpDocumentOnFile;

    return [
      this.ruleFact.createRule(`${shouldMakeHeatPumpDocumentRequired}`, [
        applyEffect(RuleAction.MAKE_INVALID),
        applyEffect(
          RuleAction.SET_ERROR_MESSAGE,
          this.submissionsConst.heatPumpDocWarning,
        ),
      ]),
      this.ruleFact.createRule(`${!shouldMakeHeatPumpDocumentRequired}`, [
        applyEffect(RuleAction.CLEAR_ERROR_MESSAGE),
      ]),
      this.ruleFact.createRule(`${shouldClearHeatPumpDocOnFile}`, [
        applyEffect(RuleAction.CLEAR_ERROR_MESSAGE),
        applyEffect(RuleAction.MAKE_NOT_REQUIRED),
      ]),
    ];
  }
  getV3CustomRules(subValues: FormGroup): FormRule[] {
    const status = subValues.get('status')?.value;
    const primarySpaceHeatingCooling = subValues.get(
      'primarySpaceHeatingCooling',
    )?.value;

    const isHeatPumpDocumentOnFile = subValues.get(
      'isHeatPumpDocumentOnFile',
    )?.value;

    const electricHeatPumpOption = 'Elec Heat Pump w/ Elec Furnace';
    const isSubmitStatus =
      status === this.submissionsConst.statusReadyToSubmit ||
      status === this.submissionsConst.statusSubmittedManually;
    const shouldShowErrorMessage =
      status === 'Ready To Submit' &&
      primarySpaceHeatingCooling === electricHeatPumpOption &&
      !isHeatPumpDocumentOnFile;
    const shouldShowRequiredPrimarySpaceHeatCooling =
      status === 'Ready To Submit' && !primarySpaceHeatingCooling;

    const shouldShowRequiredIsHeatPumpDocOnFile =
      status === 'Ready To Submit' &&
      primarySpaceHeatingCooling === electricHeatPumpOption &&
      isHeatPumpDocumentOnFile === '';

    const shouldClearOutIsHeatPumpDocOnFile =
      primarySpaceHeatingCooling !== electricHeatPumpOption;

    const shouldShowValidSave =
      (status === 'Ready To Submit' &&
        primarySpaceHeatingCooling !== electricHeatPumpOption) ||
      (primarySpaceHeatingCooling === electricHeatPumpOption &&
        isHeatPumpDocumentOnFile === true);

    const createEffectForIsHeatPumpDocOnFile = (
      action: RuleAction,
      value?: string,
    ) => this.createBaseEffect(action, 'isHeatPumpDocumentOnFile', value);

    return [
      this.ruleFact.createRule(`${shouldShowErrorMessage}`, [
        createEffectForIsHeatPumpDocOnFile(
          RuleAction.SET_ERROR_MESSAGE,
          this.submissionsConst.heatPumpDocWarning,
        ),
        createEffectForIsHeatPumpDocOnFile(RuleAction.MAKE_REQUIRED),
        createEffectForIsHeatPumpDocOnFile(RuleAction.MAKE_INVALID),
      ]),
      this.ruleFact.createRule(`${shouldShowRequiredIsHeatPumpDocOnFile}`, [
        createEffectForIsHeatPumpDocOnFile(RuleAction.MAKE_REQUIRED),
        createEffectForIsHeatPumpDocOnFile(RuleAction.MAKE_INVALID),
      ]),
      this.ruleFact.createRule(`${shouldClearOutIsHeatPumpDocOnFile}`, [
        createEffectForIsHeatPumpDocOnFile(RuleAction.MAKE_NOT_REQUIRED),
        createEffectForIsHeatPumpDocOnFile(RuleAction.CLEAR_ERROR_MESSAGE),
      ]),
      this.ruleFact.createRule(`${shouldShowValidSave}`, [
        createEffectForIsHeatPumpDocOnFile(RuleAction.CLEAR_ERROR_MESSAGE),
        createEffectForIsHeatPumpDocOnFile(RuleAction.MAKE_NOT_REQUIRED),
      ]),
      this.ruleFact.createRule(`${shouldShowRequiredPrimarySpaceHeatCooling}`, [
        this.createBaseEffect(
          RuleAction.MAKE_REQUIRED,
          'primarySpaceHeatingCooling',
        ),
        this.createBaseEffect(
          RuleAction.MAKE_INVALID,
          'primarySpaceHeatingCooling',
        ),
      ]),
      this.ruleFact.createRule(`${!isSubmitStatus}`, [
        this.createBaseEffect(
          RuleAction.CLEAR_ERROR_MESSAGE,
          'primarySpaceHeatingCooling',
        ),
        createEffectForIsHeatPumpDocOnFile(RuleAction.CLEAR_ERROR_MESSAGE),
      ]),
    ];
  }

  private createBaseEffect(
    action: RuleAction,
    controlToApplyTo: string,
    value?: string,
  ): FormRuleEffect {
    return {
      action: action,
      affectingType: 'control',
      applyTo: controlToApplyTo,
      value: value,
    };
  }
}
